// TrialStatusBanner — Phase 3 of the SaaS rollout.
// Persistent, role-aware banner that:
//   * On a healthy trial (>3 days left): subtle "X days left in trial" pill.
//   * On a trial with ≤3 days remaining: amber urgency banner.
//   * On a locked workspace (trial expired or status past_due/canceled/incomplete):
//       red lockout banner. Super Admins see "Reactivate now → /account/billing".
//       Other members see "Ask your Super Admin to reactivate".
//   * Hidden entirely when the org has an active paid subscription.
//
// Driven exclusively by RPC `get_org_subscription_state` (DB is the
// single source of truth — see Phase 3 migration).
import { useSyncExternalStore } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, Clock3, Lock } from "lucide-react";
import { usePrimaryOrgId } from "@/hooks/usePrimaryOrgId";
import { useOrgSubscriptionState } from "@/hooks/useOrgSubscriptionState";

const HIDE_ON_PATHS = ["/", "/signup", "/login", "/auth/handoff", "/account/billing", "/upgrade"];

// Phase B: subscribe to the body data-attribute set by WorkspaceLockOverlay
// so the locked-state red bar collapses to nothing while the modal-style
// overlay is on screen (avoids two competing CTAs at once).
function useWorkspaceLockOverlayMounted(): boolean {
  return useSyncExternalStore(
    (cb) => {
      if (typeof document === "undefined") return () => undefined;
      const observer = new MutationObserver(cb);
      observer.observe(document.body, { attributes: true, attributeFilter: ["data-workspace-lock-overlay"] });
      return () => observer.disconnect();
    },
    () => (typeof document !== "undefined" && document.body.hasAttribute("data-workspace-lock-overlay") ? "1" : "0"),
    () => "0",
  ) === "1";
}

export function TrialStatusBanner() {
  const location = useLocation();
  const { data: orgId } = usePrimaryOrgId();
  const { data: state } = useOrgSubscriptionState(orgId);
  const overlayMounted = useWorkspaceLockOverlayMounted();

  if (!orgId || !state) return null;
  if (state.is_management_account) return null;
  if (!state.has_subscription && !state.is_trialing && !state.is_locked) return null;
  if (state.status === "active" && !state.is_locked) return null;
  if (HIDE_ON_PATHS.includes(location.pathname)) return null;

  // Locked
  if (state.is_locked) {
    // Phase B: defer to WorkspaceLockOverlay when it's on screen.
    if (overlayMounted) return null;
    return (
      <div className="sticky top-0 z-40 bg-red-600 text-white border-b border-red-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Lock className="h-4 w-4 flex-shrink-0" />
            <p className="text-sm font-medium truncate">
              {state.status === "canceled"
                ? "Your subscription is canceled — workspace is read-only."
                : state.status === "past_due"
                ? "Payment is overdue — workspace is read-only."
                : "Your free trial has ended — workspace is read-only."}
            </p>
          </div>
          {state.is_org_super_admin ? (
            <Link
              to="/account/billing"
              className="flex-shrink-0 rounded-full bg-white text-red-600 hover:bg-red-50 text-sm font-semibold px-4 py-1.5 transition"
            >
              Reactivate now
            </Link>
          ) : (
            <span className="flex-shrink-0 text-xs text-red-100 hidden sm:inline">
              Ask your Super Admin to reactivate.
            </span>
          )}
        </div>
      </div>
    );
  }

  // Trialing
  if (state.is_trialing && state.trial_days_remaining !== null) {
    const urgent = state.trial_days_remaining <= 3;
    return (
      <div
        className={
          urgent
            ? "sticky top-0 z-40 bg-amber-600 text-white border-b border-amber-700 shadow-sm"
            : "sticky top-0 z-40 bg-[#0A3D7C] text-white border-b border-[#4A7FBF]/30 shadow-sm"
        }
      >
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {urgent ? (
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            ) : (
              <Clock3 className="h-4 w-4 flex-shrink-0 text-[#6FA0D3]" />
            )}
            <p className="text-sm font-medium truncate">
              {state.trial_days_remaining === 0
                ? "Your free trial ends today."
                : `${state.trial_days_remaining} day${state.trial_days_remaining === 1 ? "" : "s"} left in your free trial.`}
            </p>
          </div>
          {state.is_org_super_admin ? (
            <Link
              to="/upgrade"
              className={
                urgent
                  ? "flex-shrink-0 rounded-full bg-white text-amber-700 hover:bg-amber-50 text-sm font-semibold px-4 py-1.5 transition"
                  : "flex-shrink-0 rounded-full bg-[#1E5C95] hover:bg-[#2D4360] text-white text-sm font-semibold px-4 py-1.5 transition"
              }
            >
              Upgrade plan
            </Link>
          ) : (
            <span className="flex-shrink-0 text-xs hidden sm:inline opacity-90">
              Your Super Admin can upgrade.
            </span>
          )}
        </div>
      </div>
    );
  }

  return null;
}
